/******************************************************************************
 * Memokid: A game to train one's memory                                      *
 * Copyright (C) 2022 Lukas Laag                                              *
 * This file is part of Memokid                                               *
 *                                                                            *
 * Memokid is free software: you can redistribute it and/or modify            *
 * it under the terms of the GNU General Public License as published by       *
 * the Free Software Foundation, either version 3 of the License, or          *
 * (at your option) any later version.                                        *
 *                                                                            *
 * Memokid is distributed in the hope that it will be useful,                 *
 * but WITHOUT ANY WARRANTY; without even the implied warranty of             *
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the              *
 * GNU General Public License for more details.                               *
 *                                                                            *
 * You should have received a copy of the GNU General Public License          *
 * along with Memokid.  If not, see <https://www.gnu.org/licenses/>.          *
 *                                                                            *
 ******************************************************************************/

import {
  START_GAME,
  STOP_GAME,
  ADD_FIELD_CARD,
  TRANSFORM, FLIP,
  GRAB_NUPLET,
  CHANGE_PLAYER,
  AI_UPDATE,
  SETTINGS_ADD_PLAYER,
  SETTINGS_REMOVE_PLAYER,
  SETTINGS_UPDATE_PLAYER,
  SETTINGS_CHANGE_DECK,
  SETTINGS_CHANGE_MATCH_COUNT,
  SETTINGS_CHANGE_CARD_COUNT,
  SCORES_RESET,
  SCORES_ADD_GAME,
  LOAD_DECK
} from '../actions/game'
import getDecks from '../decks'
import { combineReducers } from 'redux'
import { routerReducer } from 'react-router-redux'
import { REHYDRATE } from 'redux-persist/constants'

const currentPlayer = (state, action) => {
  if (state === undefined) {
    return 0
  }
  switch (action.type) {
    case START_GAME:
      return 0
    case CHANGE_PLAYER:
      return action.playerIndex
    default:
      return state
  }
}

const mode = (state, action) => {
  if (state === undefined) {
    return false
  }
  switch (action.type) {
    case START_GAME:
      return action.mode
    case STOP_GAME:
      return 'idle'
    default:
      return state
  }
}

const decks = (state, action) => {
  if (state === undefined) {
    return []
  }
  switch (action.type) {
    case REHYDRATE:
      // Hack the decks are not persisted
      // so they need to be rebuilt when state is read
      // back from persistent storage
      if (state.length === 0) {
        return getDecks()
      }
      return state
    case LOAD_DECK:
    {
      const index = state.findIndex((deck) => action.deckId === deck.id)
      return [
        ...state.slice(0, index),
        { ...state[index], loaded: true, cards: action.cards },
        ...state.slice(index + 1)
      ]
    }
    default:
      return state
  }
}

const fieldCards = (state, action) => {
  if (state === undefined) {
    return []
  }
  switch (action.type) {
    case START_GAME:
      return []
    case ADD_FIELD_CARD:
      return [...state, action.fieldCard]
    case TRANSFORM:
      return [
        ...state.slice(0, action.index),
        { ...state[action.index], transform: action.transform, animTransform: action.animTransform },
        ...state.slice(action.index + 1)
      ]
    case FLIP:
      return [
        ...state.slice(0, action.index),
        { ...state[action.index], flipped: action.flipped, animFlipped: action.animFlipped },
        ...state.slice(action.index + 1)
      ]
    case GRAB_NUPLET:
      return state.reduce((array, card, index) => {
        if (action.fieldCardIndices.indexOf(index) === -1) {
          array.push(card)
        }
        return array
      }, [])
    default:
      return state
  }
}

const playerCards = (state, action) => {
  if (state === undefined) {
    return []
  }
  switch (action.type) {
    case GRAB_NUPLET:
    {
      const index = state.findIndex((playerCards) => action.playerId === playerCards.refid)
      return [
        ...state.slice(0, index),
        { ...state[index], cardRefids: [...state[index].cardRefids, action.cardId] },
        ...state.slice(index + 1)
      ]
    }
    case START_GAME:
      return action.playerCards
    case AI_UPDATE:
    {
      const index = state.findIndex((playerCards) => action.playerId === playerCards.refid)
      return [
        ...state.slice(0, index),
        { ...state[index], memory: action.memory },
        ...state.slice(index + 1)
      ]
    }
    default:
      return state
  }
}

const game = combineReducers({
  mode,
  currentPlayer,
  fieldCards,
  playerCards
})

const players = (state, action) => {
  if (state === undefined) {
    return []
  }
  switch (action.type) {
    case SETTINGS_ADD_PLAYER:
      return [...state, { ...action.player }]
    case SETTINGS_REMOVE_PLAYER:
    {
      const index = state.findIndex((player) => action.playerId === player.id)
      return [
        ...state.slice(0, index),
        ...state.slice(index + 1)
      ]
    }
    case SETTINGS_UPDATE_PLAYER:
    {
      const index = state.findIndex((player) => action.player.id === player.id)
      return [
        ...state.slice(0, index),
        { ...action.player },
        ...state.slice(index + 1)
      ]
    }
    default:
      return state
  }
}

const settings = (state, action) => {
  if (state === undefined) {
    return {}
  }
  switch (action.type) {
    case SETTINGS_CHANGE_DECK:
      return { ...state, deckRefid: action.deckRefid, cardCount: action.cardCount }
    case SETTINGS_CHANGE_MATCH_COUNT:
      return { ...state, matchCount: action.matchCount }
    case SETTINGS_CHANGE_CARD_COUNT:
      return { ...state, cardCount: action.cardCount }
    case SETTINGS_ADD_PLAYER:
      return { ...state, playerSeq: action.player.id }
    default:
      return state
  }
}

const scores = (state, action) => {
  if (state === undefined) {
    return []
  }
  switch (action.type) {
    case SCORES_RESET:
      return []
    case SCORES_ADD_GAME:
      // Keep only the last 10 games
      return [action.game, ...state].slice(0, 10)
    default:
      return state
  }
}

export const gameReducer = combineReducers({
  game,
  decks,
  players,
  settings,
  scores,
  routing: routerReducer
})
